import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatPhoneFR } from '../../utils/formatFR';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

/**
 * Modale de sélection de contact pour affecter un ou plusieurs biens
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - État d'ouverture de la modale
 * @param {Function} props.onClose - Callback de fermeture
 * @param {Array<string>} props.propertyIds - IDs des biens à affecter
 * @param {Function} props.onSuccess - Callback après succès
 */
const AffectClientModal = ({ isOpen, onClose, propertyIds = [], onSuccess }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Recherche avec debounce
  useEffect(() => {
    if (!isOpen) return;
    
    if (searchTerm.length >= 2) {
      const timer = setTimeout(() => {
        searchContacts();
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setContacts([]);
      setHasSearched(false);
    }
  }, [searchTerm, isOpen]);

  const searchContacts = async () => {
    setLoading(true);
    setHasSearched(true);
    try {
      const response = await axios.get(`${API}/contacts?status=active`);
      const allContacts = response.data || [];
      
      // Filtrer localement sur nom, prénom, emails ET téléphones
      const searchLower = searchTerm.toLowerCase();
      const filtered = allContacts.filter(contact => {
        const fullName = `${contact.prenom || ''} ${contact.nom || ''}`.toLowerCase();
        
        // Chercher dans tous les emails
        const emailMatch = contact.emails?.some(e => 
          (e.email || '').toLowerCase().includes(searchLower)
        ) || false;
        
        // Chercher dans tous les téléphones
        const telephoneMatch = contact.telephones?.some(t => 
          (t.numero || '').toLowerCase().includes(searchLower)
        ) || false;
        
        return fullName.includes(searchLower) || emailMatch || telephoneMatch;
      });
      
      setContacts(filtered);
    } catch (error) {
      console.error('Erreur recherche contacts:', error);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedContactId) {
      alert('Veuillez sélectionner un contact');
      return;
    }

    setSubmitting(true);
    try {
      // lot DA — registre UNIQUE : la collection des présentations, statut « sélectionné ».
      // L'ancien champ `contact.biens_presentes` n'est plus écrit (il faisait apparaître à tort
      // les biens affectés dans le bloc « Présentés »).
      let user = {};
      try { user = JSON.parse(localStorage.getItem('user') || '{}'); } catch { /* vide */ }
      for (const propertyId of propertyIds) {
        try {
          await axios.post(`${API}/contacts/${selectedContactId}/biens-presentes`, {
            bien_id: propertyId,
            statut: 'sélectionné',
            negociateur_id: user?.id || null,
          });
        } catch (e) {
          // 400 « déjà affecté » : ce n'est pas une erreur d'affectation groupée.
          if (e?.response?.status !== 400) throw e;
        }
      }

      onSuccess && onSuccess(selectedContactId);
      onClose();
    } catch (error) {
      console.error('Erreur affectation client:', error);
      alert('Erreur lors de l\'affectation du/des bien(s) au contact');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredContacts = contacts;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-slate-900">
              Affecter à un client
            </h3>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 transition-colors"
            >
              <i className="bi bi-x-lg text-xl"></i>
            </button>
          </div>
          <p className="text-sm text-slate-600 mt-1">
            {propertyIds.length === 1 
              ? 'Sélectionnez un contact pour lui présenter ce bien'
              : `Sélectionnez un contact pour lui présenter ces ${propertyIds.length} biens`
            }
          </p>
        </div>

        {/* Barre de recherche */}
        <div className="px-6 py-4 border-b border-slate-200">
          <div className="relative">
            <input
              type="text"
              placeholder="Rechercher un contact (nom, email, téléphone)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
            <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"></i>
          </div>
        </div>

        {/* Liste des contacts */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : !hasSearched ? (
            <div className="text-center py-12 text-slate-500">
              <i className="bi bi-search text-4xl mb-3"></i>
              <p>Tapez au moins 2 caractères pour rechercher</p>
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <i className="bi bi-person-x text-4xl mb-3"></i>
              <p>Aucun contact trouvé pour "{searchTerm}"</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredContacts.map((contact) => (
                <button
                  key={contact.id}
                  onClick={() => setSelectedContactId(contact.id)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                    selectedContactId === contact.id
                      ? 'border-green-500 bg-green-50'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center font-semibold">
                          {contact.prenom?.[0] || contact.nom?.[0] || '?'}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">
                            {contact.prenom || ''} {contact.nom || 'Sans nom'}
                          </p>
                          {contact.type && (
                            <span className="inline-block px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full mt-1">
                              {contact.type}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 text-sm text-slate-600 space-y-1">
                        {contact.email && (
                          <div className="flex items-center gap-2">
                            <i className="bi bi-envelope text-slate-400"></i>
                            {contact.email}
                          </div>
                        )}
                        {contact.telephone_principal && (
                          <div className="flex items-center gap-2 num">
                            <i className="bi bi-telephone text-slate-400"></i>
                            {formatPhoneFR(contact.telephone_principal)}
                          </div>
                        )}
                      </div>
                    </div>
                    {selectedContactId === contact.id && (
                      <i className="bi bi-check-circle-fill text-green-600 text-2xl"></i>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer avec actions */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 font-medium"
            disabled={submitting}
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedContactId || submitting}
            className="px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Affectation en cours...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg"></i>
                Affecter {propertyIds.length > 1 ? `les ${propertyIds.length} biens` : 'le bien'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AffectClientModal;
